import { use, useEffect, useState } from "react";
import axios from "axios";
import { Edit2, Trash2, CheckSquare, Plus, AlertCircle, CheckCircle } from "lucide-react";
import { useSelector } from "react-redux";
// Base API URL
import { useNavigate } from "react-router-dom";
const API_BASE_URL = process.env.REACT_APP_backend_url || 'http://localhost:5000/api';

function ProjectHub() {
  // State variables
  const [projectType, setProjectType] = useState("minor");
  const [projects, setProjects] = useState([]);
  const [under_review_projects, setUnderReviewProjects] = useState([]);
  const [published_projects, setPublishedProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [yearFilter, setYearFilter] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [activeTab, setActiveTab] = useState("your-projects");
  const user = useSelector((state) => state.User);
  const navigate = useNavigate();
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });

  const [formData, setFormData] = useState({
    title: "",
    roll_id: user.currentUser.rollId,
    staff_name: user.currentUser.name,
    abstract: "",
    requirements: "",
    domain: "",
    department: user.currentUser.department || "IT",
    project_type: "", // Default to minor, will be set based on projectType state
    year: new Date().getFullYear().toString(),
  });

  // Fetch projects on load and when projectType, yearFilter, or activeTab changes
  useEffect(() => {
    fetchProjects();
  }, [projectType, yearFilter, activeTab]);

  // Fetch projects from API
  const fetchProjects = async () => {
    setLoading(true);
    try {
      let endpoint = `${API_BASE_URL}/project/${user.currentUser.rollId}`;
    
      const response = await axios.get(endpoint, {
        params: {
         
          year: yearFilter || undefined,
        },
      });
      response.data.map(project => {
        if(project.year)
        {project.year=project.year.slice(0,4)}
      })
      setProjects(response.data.filter(project => project.project_type === projectType && (project.under_review === "FALSE" ) && (project.publish === "FALSE")));
      setUnderReviewProjects(response.data.filter(project =>( project.under_review=== 1 || project.under_review==="TRUE") && project.project_type === projectType));
      setPublishedProjects(response.data.filter(project => (project.publish === 1 || project.publish === "TRUE") && project.project_type === projectType));
    } catch (error) {
      showNotification("Error: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Show notification
  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Add new project
  const handleAddProject = async (e) => {
    e.preventDefault();
    setFormData(formData.project_type = projectType); // Ensure project_type is set correctly
    try {
      await axios.post(`${API_BASE_URL}/add_project`, formData);
      showNotification(`${projectType} project added successfully`);
      setShowAddModal(false);
      setFormData({
        title: "",
        roll_id:user.rollId,
        staff_name: "why u want to know",
        abstract: "",
        requirements: "",
        domain: "",
        department: "IT",
        year: new Date().getFullYear().toString(),
        project_type: projectType,
      });
      fetchProjects();
    } catch (error) {
      showNotification("Error: " + (error.response?.data?.error || error.message), "error");
    }
  };

  // Update project
  const handleUpdateProject = async (e) => {
    e.preventDefault();
    console.log("Updating project with data:", formData);
    setFormData(formData.project_type = projectType);
    try {
      await axios.put(
        `${API_BASE_URL}/projects/${projectType}/${formData.project_id}`,
        formData
      );
      showNotification(`${projectType} project updated successfully`);
      setShowEditModal(false);
      fetchProjects();
    } catch (error) {
      showNotification("Error: " + (error.response?.data?.error || error.message), "error");
    }
  };

  // Mark project as under review
  const handleReviewProject = async (project) => {
    if (!window.confirm("Are you sure you want to submit this project for review?")) return;
    try {
      await axios.post(`${API_BASE_URL}/under_review`, {
          under_review: true,
          currentUser: user.currentUser,
          project: project,
      });
      showNotification(`${projectType} project submitted for review`);
      fetchProjects();
    } catch (error) {
      showNotification("Error: " + (error.response?.data?.error || error.message), "error");
    }
  };

  // Delete project
  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/delete_project/${projectId}`);
      showNotification(`${projectType} project deleted successfully`);
      fetchProjects();
    } catch (error) {
      showNotification("Error: " + (error.response?.data?.error || error.message), "error");
    }
  };

  // Delete student from project
  const deleteStudentFromProject = async (facultyId, projectId, studentId) => {
    if (!window.confirm("Are you sure you want to remove this student from the project?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/projects/${projectType}/${projectId}/students/${studentId}`, {
        data: { faculty_id: facultyId },
      });
      showNotification("Student removed successfully");
      fetchProjects();
    } catch (error) {
      showNotification("Error: " + (error.response?.data?.error || error.message), "error");
    }
  };

  // Handle view reviews (placeholder)
  const handleViewReviews = (project) => {
     navigate(`/reviewPage/${project.project_id}`);
    // Implement review viewing logic here
  };

  // Get unique years for filter
  const uniqueYears = [...new Set(projects.map(project => project.year))].sort().reverse();
  console.log("Unique years:", uniqueYears);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <main className="container mx-auto p- relative">
        {/* Notification */}
        {notification.show && (
  <div
    className={`absolute top-4 right-4 p-3 rounded z-50 ${
      notification.type === "error"
        ? "bg-red-100 text-red-700"
        : "bg-green-100 text-green-700"
    }`}
  >
    <div className="flex items-center">
      {notification.type === "error" ? (
        <AlertCircle className="mr-2" size={20} />
      ) : (
        <CheckCircle className="mr-2" size={20} />
      )}
      <span>{notification.message}</span>
    </div>
  </div>
)}

        {/* Controls */}
        <div className="mb-6 mt-10 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1 flex">
              <button
                className={`px-4 py-2 rounded-md ${
                  projectType === "minor" ? "bg-blue-500 text-white" : "text-gray-600"
                }`}
                onClick={() => setProjectType("minor")}
              >
                Minor Projects
              </button>
              <button
                className={`px-4 py-2 rounded-md ${
                  projectType === "major" ? "bg-blue-500 text-white" : "text-gray-600"
                }`}
                onClick={() => setProjectType("major")}
              >
                Major Projects
              </button>
            </div>
            <button
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md flex items-center"
              onClick={() => setShowAddModal(true)}
            >
              <Plus className="mr-1" size={18} />
              Add Project
            </button>
          </div>
          <div className="w-full md:w-32">
            <select
              className="w-full p-2 border rounded-lg"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
            >
              <option value="">All Years</option>
              {uniqueYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1 flex mb-6">
          <button
            className={`px-4 py-2 rounded-md ${
              activeTab === "your-projects" ? "bg-blue-500 text-white" : "text-gray-600"
            }`}
            onClick={() => setActiveTab("your-projects")}
          >
            Your Projects
          </button>
          <button
            className={`px-4 py-2 rounded-md ${
              activeTab === "under-review" ? "bg-blue-500 text-white" : "text-gray-600"
            }`}
            onClick={() => setActiveTab("under-review")}
          >
            Under Review
          </button>
          <button
            className={`px-4 py-2 rounded-md ${
              activeTab === "published" ? "bg-blue-500 text-white" : "text-gray-600"
            }`}
            onClick={() => setActiveTab("published")}
          >
            Published
          </button>
        </div>

        {/* Projects List */}
        <div className="bg-gray-100 p-6 rounded-md shadow-md">
          {/* Your Projects Tab */}
          {activeTab === "your-projects" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {projects.length > 0 ? (
                projects.map((project) => (
                  <div key={project.project_id} className="project-card1 slide-in">
                    <div className="project-info">
                      <h2><strong>{project.title}</strong></h2>
                      <p><strong>Abstract:</strong> {project.abstract}</p>
                      <p><strong>Requirements:</strong> {project.requirements}</p>
                      <p><strong>Domain:</strong> {project.domain}</p>
                      <p><strong>Department:</strong> {project.department}</p>
                    
                    </div>
                    <div className="buttonns">
                      <div className="project-actions flex gap-2">
                        <button
                          onClick={() => {
                            setCurrentProject(project);
                            setFormData({
                              title: project.title,
                              roll_id: user.currentUser.rollId,
                              staff_name: user.currentUser.name,
                              abstract: project.abstract,
                              requirements: project.requirements,
                              domain: project.domain,
                              department: project.department,
                              year: project.year.toString(),
                              project_type: project.project_type,
                              project_id: project.project_id,
                              year: project.year.toString()
                            });
                            setShowEditModal(true);
                          }}
                          className="edit-btn"
                          disabled={loading}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteProject(project.project_id)}
                          className="delete-btn"
                          disabled={loading}
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => handleReviewProject(project)}
                          className="publish-btn"
                          disabled={loading}
                        >
                          Review It
                        </button>
                      </div>


                    </div>
                  </div>
                ))
              ) : (
                <div className="no-projects">
                  <h3>No Projects Available</h3>
                  <p>Click the ADD button to create a new project.</p>
                </div>
              )}
            </div>
          )}

          {/* Under Review Tab */}
          {activeTab === "under-review" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {under_review_projects.length > 0 ? (
                under_review_projects.map((project) => (
                  <div key={project.project_id} className="project-card slide-in">
                    <div className="project-info reviews">
                      <h2>{project.title}</h2>
                      {project.abstract && <p><strong>Abstract:</strong> {project.abstract}</p>}
                      {project.requirements && <p><strong>Requirements:</strong> {project.requirements}</p>}
                      {project.domain && <p><strong>Domain:</strong> {project.domain}</p>}
                      {project.department && <p><strong>Department:</strong> {project.department}</p>}
                      <button
                        onClick={() => handleViewReviews(project)}
                        className="button btn-success"
                        disabled={loading}
                      >
                        View Reviews
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-projects">
                  <h3>No Projects Under Review</h3>
                  <p>Submit projects for review from the Your Projects tab.</p>
                </div>
              )}
            </div>
          )}

          {/* Published Projects Tab */}
          {activeTab === "published" && (
            <div className="published-project-list">
              {published_projects.length > 0 ? (
                published_projects.map((project) => (
                  <div key={project.project_id} className="project-card">
                    <div className="project-info">
                      <h2>{project.title}</h2>
                      {project.abstract && <p><strong>Abstract:</strong> {project.abstract}</p>}
                      {project.requirements && <p><strong>Requirements:</strong> {project.requirements}</p>}
                      {project.domain && <p><strong>Domain:</strong> {project.domain}</p>}
                      {project.department && <p><strong>Department:</strong> {project.department}</p>}
                      {project.students?.length > 0 ? (
                        <>
                          <p><strong>Students:</strong></p>
                          <ul>
                            {project.students.map((student) => (
                              <div key={student.id} className="flex gap-3 text-lg">
                                <li>
                                  {student.name} - {student.email} (ID: {student.student_id})
                                </li>
                                <button
                                  className="bg-red-500 rounded-md p-2"
                                  onClick={() => deleteStudentFromProject('123', project.project_id, student.student_id)}
                                  disabled={loading}
                                >
                                  Delete
                                </button>
                              </div>
                            ))}
                          </ul>
                        </>
                      ) : (
                        <p><strong>Students:</strong> No students assigned</p>
                      )}
                      <button
                        className="view-reviews"
                        onClick={() => handleViewReviews(project)}
                        disabled={loading}
                      >
                        View Reviews
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-projects">
                  <h3>No Published Projects</h3>
                  <p>Projects that pass review will appear here.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Project Modal */}
        {showAddModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowAddModal(false)}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowAddModal(false)}
                className="absolute top-4 right-4 text-gray-500 text-2xl font-bold"
              >
                ×
              </button>
              <h2 className="text-xl font-bold mb-4">
                Add {projectType === "minor" ? "Minor" : "Major"} Project
              </h2>
              <form onSubmit={handleAddProject}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title*</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year*</label>
                    <input
                      type="number"
                      min="2000"
                      max={new Date().getFullYear()}
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                    <input
                      type="text"
                      name="domain"
                      value={formData.domain}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Requirements
                  </label>
                  <textarea
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded h-20"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Abstract</label>
                  <textarea
                    name="abstract"
                    value={formData.abstract}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded h-32"
                  ></textarea>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Add Project
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Project Modal */}
        {showEditModal && currentProject && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowEditModal(false)}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowEditModal(false)}
                className="absolute top-4 right-4 text-gray-500 text-2xl font-bold"
              >
                ×
              </button>
              <h2 className="text-xl font-bold mb-4">
                Edit {projectType === "minor" ? "Minor" : "Major"} Project
              </h2>
              <form onSubmit={handleUpdateProject}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title*</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year*</label>
                    <input
                      type="number"
                      min="2000"
                      max={new Date().getFullYear()}
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                    <input
                      type="text"
                      name="domain"
                      value={formData.domain}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Requirements
                  </label>
                  <textarea
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded h-20"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Abstract</label>
                  <textarea
                    name="abstract"
                    value={formData.abstract}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded h-32"
                  ></textarea>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Update Project
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default ProjectHub;